import { db } from './firebase'
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  deleteDoc, 
  doc, 
  writeBatch,
  orderBy,
  limit
} from 'firebase/firestore'

class AccountDeletionService {
  constructor() {
    this.batchSize = 500 // Firestore batch limit
  }

  /**
   * Delete all user data from Firestore
   * @param {string} userId - User ID to delete
   * @param {string} userEmail - User email for logging
   * @returns {Promise<Object>} Deletion results
   */
  async deleteUserAccount(userId, userEmail) {
    console.log(`🗑️ Starting account deletion for user: ${userId} (${userEmail})`)
    
    const deletionResults = {
      userId,
      userEmail,
      timestamp: new Date().toISOString(),
      collections: {},
      errors: [],
      totalDocumentsDeleted: 0,
      success: false
    }

    try {
      // Verify user exists before starting deletion
      const userRef = doc(db, 'users', userId)
      const userDoc = await getDoc(userRef)
      
      if (!userDoc.exists()) {
        throw new Error(`User document ${userId} does not exist`)
      }
      
      console.log(`✅ User document exists, proceeding with deletion`)

      // Delete in order of dependencies (user document last to maintain auth state)
      const deletionSteps = [
        { name: 'alerts', fn: () => this.deleteUserAlerts(userId, deletionResults) },
        { name: 'scheduledAlerts', fn: () => this.deleteUserScheduledAlerts(userId, deletionResults) },
        { name: 'calendarEvents', fn: () => this.deleteUserCalendarEvents(userId, deletionResults) },
        { name: 'groupMemberships', fn: () => this.deleteUserGroupMemberships(userId, deletionResults) },
        { name: 'organizationMemberships', fn: () => this.deleteUserOrganizationMemberships(userId, deletionResults) },
        { name: 'notificationPreferences', fn: () => this.deleteUserNotificationPreferences(userId, deletionResults) },
        { name: 'subscriptions', fn: () => this.deleteUserSubscriptionData(userId, deletionResults) },
        { name: 'emailStats', fn: () => this.deleteUserEmailStats(userId, deletionResults) },
        { name: 'organizationRequests', fn: () => this.deleteUserOrganizationRequests(userId, deletionResults) },
        { name: 'groupAccess', fn: () => this.deleteUserGroupAccessRecords(userId, deletionResults) },
        { name: 'followRelationships', fn: () => this.deleteUserFollowRelationships(userId, deletionResults) },
        { name: 'userDocument', fn: () => this.deleteUserDocument(userId, deletionResults) }
      ]

      // Execute each deletion step
      for (const step of deletionSteps) {
        try {
          console.log(`🔄 Executing deletion step: ${step.name}`)
          await step.fn()
          console.log(`✅ Completed deletion step: ${step.name}`)
        } catch (stepError) {
          console.error(`❌ Error in deletion step ${step.name}:`, stepError)
          deletionResults.errors.push({
            step: step.name,
            error: stepError.message,
            timestamp: new Date().toISOString()
          })
          // Continue with other steps even if one fails
        }
      }

      deletionResults.success = true
      console.log(`✅ Account deletion completed for user: ${userId}`)
      console.log(`📊 Total documents deleted: ${deletionResults.totalDocumentsDeleted}`)
      console.log(`❌ Errors encountered: ${deletionResults.errors.length}`)
      
      return deletionResults

    } catch (error) {
      console.error(`❌ Critical error during account deletion for user ${userId}:`, error)
      deletionResults.errors.push({
        step: 'critical_error',
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      })
      deletionResults.success = false
      
      // Don't throw the error, return the results so we can see what happened
      return deletionResults
    }
  }

  /**
   * Delete main user document
   */
  async deleteUserDocument(userId, deletionResults) {
    try {
      console.log(`🔍 Attempting to delete user document: ${userId}`)
      const userRef = doc(db, 'users', userId)
      
      // Check if document exists first
      const userDoc = await getDoc(userRef)
      if (!userDoc.exists()) {
        console.log(`⚠️ User document ${userId} does not exist`)
        deletionResults.collections.users = 0
        return
      }
      
      console.log(`🔍 User document exists, deleting...`)
      await deleteDoc(userRef)
      
      deletionResults.collections.users = 1
      deletionResults.totalDocumentsDeleted += 1
      console.log(`✅ Successfully deleted user document: ${userId}`)
    } catch (error) {
      console.error(`❌ Error deleting user document:`, error)
      deletionResults.errors.push({
        step: 'delete_user_document',
        error: error.message,
        userId: userId
      })
    }
  }

  /**
   * Delete user's alerts
   */
  async deleteUserAlerts(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Delete alerts where user is the creator
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('postedByUserId', '==', userId)
      )
      
      const alertsSnapshot = await getDocs(alertsQuery)
      const batch = writeBatch(db)
      
      alertsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
        deletedCount++
      })
      
      if (deletedCount > 0) {
        await batch.commit()
      }
      
      deletionResults.collections.alerts = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} alerts created by user`)
    } catch (error) {
      console.error(`❌ Error deleting user alerts:`, error)
      deletionResults.errors.push({
        step: 'delete_user_alerts',
        error: error.message
      })
    }
  }

  /**
   * Delete user's scheduled alerts
   */
  async deleteUserScheduledAlerts(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Delete scheduled alerts where user is the creator
      const scheduledAlertsQuery = query(
        collection(db, 'scheduledAlerts'),
        where('postedByUserId', '==', userId)
      )
      
      const scheduledAlertsSnapshot = await getDocs(scheduledAlertsQuery)
      const batch = writeBatch(db)
      
      scheduledAlertsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
        deletedCount++
      })
      
      if (deletedCount > 0) {
        await batch.commit()
      }
      
      deletionResults.collections.scheduledAlerts = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} scheduled alerts created by user`)
    } catch (error) {
      console.error(`❌ Error deleting user scheduled alerts:`, error)
      deletionResults.errors.push({
        step: 'delete_user_scheduled_alerts',
        error: error.message
      })
    }
  }

  /**
   * Delete user's calendar events
   */
  async deleteUserCalendarEvents(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Delete calendar events where user is the creator
      const eventsQuery = query(
        collection(db, 'calendarEvents'),
        where('createdByUserId', '==', userId)
      )
      
      const eventsSnapshot = await getDocs(eventsQuery)
      const batch = writeBatch(db)
      
      eventsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
        deletedCount++
      })
      
      if (deletedCount > 0) {
        await batch.commit()
      }
      
      deletionResults.collections.calendarEvents = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} calendar events created by user`)
    } catch (error) {
      console.error(`❌ Error deleting user calendar events:`, error)
      deletionResults.errors.push({
        step: 'delete_user_calendar_events',
        error: error.message
      })
    }
  }

  /**
   * Delete user's group memberships
   */
  async deleteUserGroupMemberships(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Get all organizations to check for group memberships
      const organizationsSnapshot = await getDocs(collection(db, 'organizations'))
      
      for (const orgDoc of organizationsSnapshot.docs) {
        const orgId = orgDoc.id
        
        // Get all groups in this organization
        const groupsSnapshot = await getDocs(
          collection(db, 'organizations', orgId, 'groups')
        )
        
        for (const groupDoc of groupsSnapshot.docs) {
          const groupId = groupDoc.id
          
          // Delete user from group followers
          const groupFollowersRef = doc(db, 'organizations', orgId, 'groups', groupId, 'followers', userId)
          try {
            await deleteDoc(groupFollowersRef)
            deletedCount++
          } catch (error) {
            // Document might not exist, which is fine
          }
        }
      }
      
      deletionResults.collections.groupMemberships = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} group memberships`)
    } catch (error) {
      console.error(`❌ Error deleting user group memberships:`, error)
      deletionResults.errors.push({
        step: 'delete_user_group_memberships',
        error: error.message
      })
    }
  }

  /**
   * Delete user's organization memberships
   */
  async deleteUserOrganizationMemberships(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Get all organizations to check for user memberships
      const organizationsSnapshot = await getDocs(collection(db, 'organizations'))
      
      for (const orgDoc of organizationsSnapshot.docs) {
        const orgId = orgDoc.id
        
        // Delete user from organization followers
        const orgFollowersRef = doc(db, 'organizations', orgId, 'followers', userId)
        try {
          await deleteDoc(orgFollowersRef)
          deletedCount++
        } catch (error) {
          // Document might not exist, which is fine
        }
      }
      
      deletionResults.collections.organizationMemberships = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} organization memberships`)
    } catch (error) {
      console.error(`❌ Error deleting user organization memberships:`, error)
      deletionResults.errors.push({
        step: 'delete_user_organization_memberships',
        error: error.message
      })
    }
  }

  /**
   * Delete user's notification preferences
   */
  async deleteUserNotificationPreferences(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Delete notification preferences subcollection
      const preferencesRef = doc(db, 'users', userId, 'preferences', 'notifications')
      try {
        await deleteDoc(preferencesRef)
        deletedCount++
      } catch (error) {
        // Document might not exist, which is fine
      }
      
      deletionResults.collections.notificationPreferences = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} notification preferences`)
    } catch (error) {
      console.error(`❌ Error deleting user notification preferences:`, error)
      deletionResults.errors.push({
        step: 'delete_user_notification_preferences',
        error: error.message
      })
    }
  }

  /**
   * Delete user's subscription data
   */
  async deleteUserSubscriptionData(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Delete user's subscription subcollection
      const subscriptionsSnapshot = await getDocs(
        collection(db, 'users', userId, 'subscriptions')
      )
      
      const batch = writeBatch(db)
      subscriptionsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
        deletedCount++
      })
      
      if (deletedCount > 0) {
        await batch.commit()
      }
      
      // Also delete from main subscriptions collection
      const mainSubscriptionsQuery = query(
        collection(db, 'subscriptions'),
        where('userId', '==', userId)
      )
      
      const mainSubscriptionsSnapshot = await getDocs(mainSubscriptionsQuery)
      const mainBatch = writeBatch(db)
      
      mainSubscriptionsSnapshot.docs.forEach((doc) => {
        mainBatch.delete(doc.ref)
        deletedCount++
      })
      
      if (mainSubscriptionsSnapshot.docs.length > 0) {
        await mainBatch.commit()
      }
      
      deletionResults.collections.subscriptions = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} subscription records`)
    } catch (error) {
      console.error(`❌ Error deleting user subscription data:`, error)
      deletionResults.errors.push({
        step: 'delete_user_subscription_data',
        error: error.message
      })
    }
  }

  /**
   * Delete user's email stats
   */
  async deleteUserEmailStats(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Delete email stats where user is the recipient
      const emailStatsQuery = query(
        collection(db, 'emailStats'),
        where('recipientId', '==', userId)
      )
      
      const emailStatsSnapshot = await getDocs(emailStatsQuery)
      const batch = writeBatch(db)
      
      emailStatsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
        deletedCount++
      })
      
      if (deletedCount > 0) {
        await batch.commit()
      }
      
      deletionResults.collections.emailStats = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} email stats records`)
    } catch (error) {
      console.error(`❌ Error deleting user email stats:`, error)
      deletionResults.errors.push({
        step: 'delete_user_email_stats',
        error: error.message
      })
    }
  }

  /**
   * Delete user's organization requests
   */
  async deleteUserOrganizationRequests(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Delete organization requests where user is the requester
      const orgRequestsQuery = query(
        collection(db, 'organizationRequests'),
        where('userId', '==', userId)
      )
      
      const orgRequestsSnapshot = await getDocs(orgRequestsQuery)
      const batch = writeBatch(db)
      
      orgRequestsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
        deletedCount++
      })
      
      if (deletedCount > 0) {
        await batch.commit()
      }
      
      deletionResults.collections.organizationRequests = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} organization requests`)
    } catch (error) {
      console.error(`❌ Error deleting user organization requests:`, error)
      deletionResults.errors.push({
        step: 'delete_user_organization_requests',
        error: error.message
      })
    }
  }

  /**
   * Delete user's group access records
   */
  async deleteUserGroupAccessRecords(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Delete group access records for this user
      const groupAccessQuery = query(
        collection(db, 'group_access'),
        where('userId', '==', userId)
      )
      
      const groupAccessSnapshot = await getDocs(groupAccessQuery)
      const batch = writeBatch(db)
      
      groupAccessSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
        deletedCount++
      })
      
      if (deletedCount > 0) {
        await batch.commit()
      }
      
      deletionResults.collections.groupAccess = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted ${deletedCount} group access records`)
    } catch (error) {
      console.error(`❌ Error deleting user group access records:`, error)
      deletionResults.errors.push({
        step: 'delete_user_group_access_records',
        error: error.message
      })
    }
  }

  /**
   * Delete user's follow relationships
   */
  async deleteUserFollowRelationships(userId, deletionResults) {
    try {
      let deletedCount = 0
      
      // Delete user's followed organizations from their profile
      // This is handled by deleting the user document, but we'll log it
      deletionResults.collections.followRelationships = deletedCount
      deletionResults.totalDocumentsDeleted += deletedCount
      console.log(`✅ Deleted follow relationships (handled by user document deletion)`)
    } catch (error) {
      console.error(`❌ Error deleting user follow relationships:`, error)
      deletionResults.errors.push({
        step: 'delete_user_follow_relationships',
        error: error.message
      })
    }
  }

  /**
   * Get account deletion preview (what will be deleted)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Preview of what will be deleted
   */
  async getAccountDeletionPreview(userId) {
    console.log(`🔍 Getting account deletion preview for user: ${userId}`)
    
    const preview = {
      userId,
      collections: {},
      totalDocuments: 0,
      timestamp: new Date().toISOString()
    }

    try {
      // Count user document
      preview.collections.users = 1
      preview.totalDocuments += 1

      // Count alerts
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('postedByUserId', '==', userId)
      )
      const alertsSnapshot = await getDocs(alertsQuery)
      preview.collections.alerts = alertsSnapshot.docs.length
      preview.totalDocuments += alertsSnapshot.docs.length

      // Count scheduled alerts
      const scheduledAlertsQuery = query(
        collection(db, 'scheduledAlerts'),
        where('postedByUserId', '==', userId)
      )
      const scheduledAlertsSnapshot = await getDocs(scheduledAlertsQuery)
      preview.collections.scheduledAlerts = scheduledAlertsSnapshot.docs.length
      preview.totalDocuments += scheduledAlertsSnapshot.docs.length

      // Count calendar events
      const eventsQuery = query(
        collection(db, 'calendarEvents'),
        where('createdByUserId', '==', userId)
      )
      const eventsSnapshot = await getDocs(eventsQuery)
      preview.collections.calendarEvents = eventsSnapshot.docs.length
      preview.totalDocuments += eventsSnapshot.docs.length

      // Count subscriptions
      const subscriptionsQuery = query(
        collection(db, 'subscriptions'),
        where('userId', '==', userId)
      )
      const subscriptionsSnapshot = await getDocs(subscriptionsQuery)
      preview.collections.subscriptions = subscriptionsSnapshot.docs.length
      preview.totalDocuments += subscriptionsSnapshot.docs.length

      // Count organization requests
      const orgRequestsQuery = query(
        collection(db, 'organizationRequests'),
        where('userId', '==', userId)
      )
      const orgRequestsSnapshot = await getDocs(orgRequestsQuery)
      preview.collections.organizationRequests = orgRequestsSnapshot.docs.length
      preview.totalDocuments += orgRequestsSnapshot.docs.length

      console.log(`📊 Account deletion preview: ${preview.totalDocuments} documents will be deleted`)
      return preview

    } catch (error) {
      console.error(`❌ Error getting account deletion preview:`, error)
      throw error
    }
  }
}

export default new AccountDeletionService()
